"use client";

import { useEffect, useState, useRef, forwardRef, useImperativeHandle } from "react";
import Turnstile, { useTurnstile } from "react-turnstile";
import { ShieldAlert, RefreshCw } from "lucide-react";

import { environmentConfig, isDevelopment } from "@/app/_module/utils/environment-config";

interface TurnstileWidgetProps {
  onToken: (token: string) => void;
  setError: (error: string) => void;
}

export interface TurnstileWidgetRef {
  reset: () => void;
}

const TurnstileWidget = forwardRef<TurnstileWidgetRef, TurnstileWidgetProps>(
  function TurnstileWidget({ onToken, setError }, ref) {
    const turnstile = useTurnstile();

    // Cloudflare's script is blocked often enough (Safari ITP, content blockers,
    // corporate DNS) that a silent failure strands the user: the sign-in button
    // stays disabled and there is nothing to click. We keep the gate exactly as
    // it is — no token still means no sign-in — but give a way to recover.
    //   1st failure -> remount the widget once, quietly (most are transient)
    //   2nd failure -> render a panel explaining it, with Try again / Reload
    const [loadFailed, setLoadFailed] = useState(false);
    const [attempt, setAttempt] = useState(0);
    const autoRetried = useRef(false);

    // Expose reset function to parent component
    useImperativeHandle(ref, () => ({
      reset: () => {
        if (turnstile) {
          turnstile.reset();
        }
      },
    }), [turnstile]);

    // react-turnstile rejects a promise when the script itself can't load.
    useEffect(() => {
      const handler = (event: PromiseRejectionEvent) => {
        if (event.reason === 'Failed to load Turnstile.') {
          event.preventDefault();

          if (!autoRetried.current) {
            // Quiet first retry — no scary copy for a blip that fixes itself.
            autoRetried.current = true;
            setError("");
            window.setTimeout(() => setAttempt((a) => a + 1), 1500);
            return;
          }

          // Second failure: own the message in our panel rather than the
          // page-level error banner, so the recovery actions sit next to it.
          setLoadFailed(true);
          setError("");
        }
      };
      window.addEventListener('unhandledrejection', handler);
      return () => window.removeEventListener('unhandledrejection', handler);
    }, [setError]);

    // Skip Turnstile in development - auto-approve with dummy token
    useEffect(() => {
      if (isDevelopment()) {
        // Automatically call onToken with a dummy token in development
        onToken("dev-bypass-token");
      }
    }, [onToken]);

    if (isDevelopment()) {
      return null;
    }

    const siteKey = environmentConfig.NEXT_PUBLIC_TURNSTILE_SITE_KEY || process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY || process.env.NEXT_PUBLIC_CLOUDFLARE_SITE_KEY;

    if (!siteKey) {
      return null;
    }

    if (loadFailed) {
      const retry = () => {
        setLoadFailed(false);
        autoRetried.current = false;
        onToken("");
        setAttempt((a) => a + 1);
      };

      return (
        <div className="w-full rounded-lg border border-amber-300 bg-amber-50 p-4 text-center">
          <ShieldAlert className="mx-auto h-5 w-5 text-amber-600" aria-hidden="true" />
          <p className="mt-2 text-sm font-medium text-amber-900">
            Security check couldn&apos;t load
          </p>
          <p className="mt-1 text-xs text-amber-800">
            An ad blocker or privacy setting is usually the cause. Allow{" "}
            <span className="font-mono">challenges.cloudflare.com</span>, then try again.
          </p>
          <div className="mt-3 flex justify-center gap-2">
            <button
              type="button"
              onClick={retry}
              className="inline-flex items-center gap-1.5 rounded-md bg-amber-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-amber-700"
            >
              <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" />
              Try again
            </button>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="rounded-md border border-amber-400 bg-white px-3 py-1.5 text-xs font-medium text-amber-900 hover:bg-amber-100"
            >
              Reload page
            </button>
          </div>
        </div>
      );
    }

    // min-h reserves the widget's space so the form does not jump when it
    // loads — the reason `fixedSize` was here — WITHOUT capping its height.
    // See the note on the removed prop below.
    return (
      <div className="flex w-full justify-center min-h-[65px]">
        <Turnstile
          // Bumping the key remounts the widget, which re-attempts the script
          // load — `retry` below only covers challenge failures, not load ones.
          key={attempt}
          // ⚠️ `fixedSize` REMOVED (2026-08-31). It pinned the container to
          // exactly 300x65px (react-turnstile hardcodes that height), and the
          // solved state renders taller — logo plus the "Privacy • Help" line —
          // so the bottom of the widget was clipped on mobile.
          // Do not add it back to stop layout shift; the wrapper's min-h does
          // that without imposing a ceiling Cloudflare does not agree to.
          onError={() => {
            setError("CAPTCHA verification failed. Please try again.");
          }}
          onVerify={onToken}
          sitekey={siteKey}
          theme="light"
          refreshExpired="auto"
          retry="auto"
          retryInterval={3000}
          onExpire={() => {
            setError("CAPTCHA verification expired, please try again");
            onToken("");
          }}
        />
      </div>
    );
  }
);

export default TurnstileWidget;
